import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface Rule {
  id: string;
  text: string;
  category: RuleCategory;
  createdAt: string;
}

export type RuleCategory = 'deployment' | 'architecture' | 'security' | 'testing' | 'process' | 'other';

export const RULE_CATEGORIES: { value: RuleCategory; label: string }[] = [
  { value: 'deployment', label: 'Deployment' },
  { value: 'architecture', label: 'Architecture' },
  { value: 'security', label: 'Security' },
  { value: 'testing', label: 'Testing' },
  { value: 'process', label: 'Process' },
  { value: 'other', label: 'Other' },
];

export interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  prompt: string;
  createdAt: string;
}

export const rulesPromptsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  // Rules
  async listRules(): Promise<Rule[]> {
    const { data } = await api.get('/standards/rules');
    return data;
  },

  async addRule(rule: Omit<Rule, 'id' | 'createdAt'>): Promise<Rule> {
    const { data } = await api.post('/standards/rules', rule);
    return data;
  },

  async updateRule(id: string, rule: Partial<Omit<Rule, 'id' | 'createdAt'>>): Promise<Rule> {
    const { data } = await api.put(`/standards/rules/${id}`, rule);
    return data;
  },

  async deleteRule(id: string): Promise<void> {
    await api.delete(`/standards/rules/${id}`);
  },

  // Prompt Templates
  async listPromptTemplates(): Promise<PromptTemplate[]> {
    const { data } = await api.get('/standards/prompt-templates');
    return data;
  },

  async addPromptTemplate(template: Omit<PromptTemplate, 'id' | 'createdAt'>): Promise<PromptTemplate> {
    const { data } = await api.post('/standards/prompt-templates', template);
    return data;
  },

  async updatePromptTemplate(id: string, template: Partial<Omit<PromptTemplate, 'id' | 'createdAt'>>): Promise<PromptTemplate> {
    const { data } = await api.put(`/standards/prompt-templates/${id}`, template);
    return data;
  },

  async deletePromptTemplate(id: string): Promise<void> {
    await api.delete(`/standards/prompt-templates/${id}`);
  },
};
